'use client';
import React, { useState, useRef, useCallback } from 'react';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export default function ExtractPDFTextTool() {
  const [file, setFile] = useState<File | null>(null);
  const [extractedText, setExtractedText] = useState('');
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [copied, setCopied] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) { setFile(f); setExtractedText(''); }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f?.type === 'application/pdf') { setFile(f); setExtractedText(''); }
    else alert('Please upload a PDF file.');
  };

  const handleExtract = async () => {
    if (!file) return;
    setProcessing(true);
    setProgress(0);
    setExtractedText('');
    try {
      const pdfjsLib = await import('pdfjs-dist');
      pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      let fullText = '';
      for (let i = 1; i <= pdf.numPages; i++) {
        setProgress(Math.round((i / pdf.numPages) * 100));
        const page = await pdf.getPage(i);
        const content = await page.getTextContent();
        const pageText = content.items.map((item: { str?: string }) => item.str || '').join(' ');
        fullText += `--- Page ${i} ---\n${pageText}\n\n`;
      }
      setExtractedText(fullText.trim() || 'No text found in this PDF. It may be a scanned image-based PDF.');
    } catch (err) {
      console.error(err);
      alert('Failed to extract text. Please try again.');
    } finally {
      setProcessing(false);
      setProgress(0);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(extractedText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleDownload = () => {
    const blob = new Blob([extractedText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${file?.name.replace('.pdf', '') || 'extracted'}-text.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5">
      {!file ? (
        <div
          className={`upload-zone rounded-2xl p-10 text-center cursor-pointer ${isDragging ? 'drag-over' : ''}`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
            </svg>
          </div>
          <p className="font-semibold text-text-primary mb-1">Upload PDF</p>
          <p className="text-text-muted text-sm">Drag & drop or click to browse</p>
          <input ref={fileInputRef} type="file" accept="application/pdf" className="hidden" onChange={handleFileChange} />
        </div>
      ) : (
        <div className="space-y-5">
          <div className="flex items-center justify-between bg-surface rounded-xl p-3">
            <div>
              <p className="font-semibold text-text-primary text-sm">{file.name}</p>
              <p className="text-text-muted text-xs">{formatBytes(file.size)}</p>
            </div>
            <button onClick={() => { setFile(null); setExtractedText(''); if (fileInputRef.current) fileInputRef.current.value = ''; }} className="text-text-muted hover:text-red-500 transition-colors text-xs">Remove</button>
          </div>

          {processing && (
            <div>
              <div className="flex justify-between text-xs text-text-muted mb-1"><span>Extracting text…</span><span>{progress}%</span></div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-brand-blue rounded-full transition-all duration-300" style={{ width: `${progress}%` }} /></div>
            </div>
          )}

          {!extractedText && (
            <button
              onClick={handleExtract}
              disabled={processing}
              className="btn-primary w-full text-base py-3.5 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {processing ? (
                <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Extracting…</>
              ) : (
                'Extract Text from PDF'
              )}
            </button>
          )}

          {extractedText && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="font-semibold text-text-primary text-sm">Extracted Text</p>
                <div className="flex gap-2">
                  <button onClick={handleCopy} className="text-xs px-3 py-1.5 rounded-lg bg-blue-50 text-brand-blue hover:bg-blue-100 transition-colors font-medium">
                    {copied ? '✓ Copied' : 'Copy'}
                  </button>
                  <button onClick={handleDownload} className="text-xs px-3 py-1.5 rounded-lg bg-green-50 text-green-700 hover:bg-green-100 transition-colors font-medium">
                    Download .txt
                  </button>
                </div>
              </div>
              <textarea
                readOnly
                value={extractedText}
                className="w-full h-64 border border-gray-200 rounded-xl px-4 py-3 text-sm text-text-secondary font-mono resize-none focus:outline-none bg-gray-50"
              />
              <p className="text-xs text-text-muted">{extractedText.split(/\s+/).filter(Boolean).length} words · {extractedText.length} characters</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
